import { mergeAttributes, Node } from "@tiptap/core"
import type { DOMOutputSpec } from "@tiptap/pm/model"

// ── Types ────────────────────────────────────────────────────────────────────

export interface EmbedParseResult {
  providerName: string
  url: string
  embedId: string
  embedType: string | null
  embedUrl: string
  thumbnailUrl?: string
  title?: string
}

export interface EmbedData {
  url: string | null
  providerName: string | null
  embedId: string | null
  embedType: string | null
  embedUrl: string | null
  embedHtml: string | null
  oembedType: string | null
  thumbnailUrl: string | null
  faviconUrl: string | null
  title: string | null
  description: string | null
  embedWidth: number | null
  embedHeight: number | null
  thumbnailWidth: number | null
  thumbnailHeight: number | null
  displayMode: string | null
}

export type EmbedEmailRender =
  | {
      type: "image-link"
      href: string
      imageUrl: string | null
      imageAlt: string
      playLabel: string
      faviconUrl: string | null
    }
  | {
      type: "styled-card"
      href: string
      imageUrl: string | null
      title: string
      description: string | null
      faviconUrl: string | null
    }
  | {
      type: "link"
      href: string
    }

export type EmbedDomSpec = DOMOutputSpec

// ── Helpers ──────────────────────────────────────────────────────────────────

function getHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return ""
  }
}

function buildSrcdocHtml(html: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}</style></head><body>${html}</body></html>`
}

function domSpecToNode(spec: EmbedDomSpec) {
  if (typeof spec === "string") return document.createTextNode(spec)
  const [tag, attrs, ...children] = spec as [
    string,
    Record<string, string>,
    ...EmbedDomSpec[],
  ]
  const el = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
  for (const child of children) el.appendChild(domSpecToNode(child))
  return el
}

function getAspectRatioStyle(data: EmbedData): string {
  if (data.embedWidth && data.embedHeight)
    return `aspect-ratio: ${data.embedWidth} / ${data.embedHeight};`
  if (data.embedHeight) return `height: ${data.embedHeight}px;`
  return "aspect-ratio: 16 / 9;"
}

function renderIframe(data: EmbedData): EmbedDomSpec {
  return [
    "div",
    { class: "embed-preview" },
    [
      "iframe",
      {
        class: "embed-oembed-iframe",
        src: data.embedUrl ?? "",
        style: getAspectRatioStyle(data),
        sandbox:
          "allow-scripts allow-same-origin allow-forms allow-presentation",
        allow:
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen",
        allowfullscreen: "",
        loading: "lazy",
        title: data.title || "Embedded content",
      },
    ],
  ]
}

function renderSrcdocIframe(data: EmbedData): EmbedDomSpec {
  return [
    "div",
    { class: "embed-preview" },
    [
      "iframe",
      {
        class: "embed-oembed-iframe",
        srcdoc: buildSrcdocHtml(data.embedHtml ?? ""),
        style: getAspectRatioStyle(data),
        sandbox: "allow-scripts",
        loading: "lazy",
        title: data.title || "Embedded content",
      },
    ],
  ]
}

function renderMetadataCard(data: EmbedData): EmbedDomSpec {
  const url = data.url || ""
  const hostname = getHostname(url)

  const bodyChildren: EmbedDomSpec[] = []
  if (data.title)
    bodyChildren.push(["span", { class: "embed-link-card-title" }, data.title])
  if (data.description)
    bodyChildren.push([
      "span",
      { class: "embed-link-card-description" },
      data.description,
    ])

  const footerChildren: EmbedDomSpec[] = []
  if (data.faviconUrl)
    footerChildren.push([
      "img",
      {
        class: "embed-link-card-favicon",
        src: data.faviconUrl,
        alt: "",
        loading: "lazy",
      },
    ])
  footerChildren.push([
    "span",
    { class: "embed-link-card-hostname" },
    hostname || url,
  ])
  bodyChildren.push([
    "div",
    { class: "embed-link-card-footer" },
    ...footerChildren,
  ])

  const cardChildren: EmbedDomSpec[] = [
    ["div", { class: "embed-link-card-body" }, ...bodyChildren],
  ]
  if (data.thumbnailUrl)
    cardChildren.unshift([
      "img",
      {
        class: "embed-link-card-image",
        src: data.thumbnailUrl,
        alt: data.title || "",
        loading: "lazy",
      },
    ])

  return [
    "div",
    { class: "embed-preview" },
    [
      "a",
      {
        class: "embed-link-card",
        href: url,
        target: "_blank",
        rel: "nofollow ugc noopener",
      },
      ...cardChildren,
    ],
  ]
}

const PLAY_ICON: EmbedDomSpec = [
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "32",
    height: "32",
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": "true",
  },
  ["polygon", { points: "5 3 19 12 5 21" }],
]

function renderVideoThumbnail(data: EmbedData): EmbedDomSpec {
  const linkChildren: EmbedDomSpec[] = [
    [
      "img",
      {
        class: "embed-video-thumbnail-img",
        src: data.thumbnailUrl ?? "",
        alt: data.title || "",
        loading: "lazy",
      },
    ],
    ["div", { class: "embed-video-play-button" }, PLAY_ICON],
  ]

  if (data.faviconUrl)
    linkChildren.push([
      "img",
      {
        class: "embed-video-favicon",
        src: data.faviconUrl,
        alt: "",
        loading: "lazy",
      },
    ])

  const thumbnailStyle =
    data.thumbnailWidth && data.thumbnailHeight
      ? `aspect-ratio: ${data.thumbnailWidth} / ${data.thumbnailHeight}; width: min(100%, calc(800px * ${data.thumbnailWidth} / ${data.thumbnailHeight}));`
      : ""

  return [
    "div",
    { class: "embed-preview" },
    [
      "a",
      {
        class: "embed-video-thumbnail",
        href: data.url ?? "",
        target: "_blank",
        rel: "nofollow ugc noopener",
        ...(thumbnailStyle ? { style: thumbnailStyle } : {}),
      },
      ...linkChildren,
    ],
  ]
}

function renderPhotoEmbed(data: EmbedData): EmbedDomSpec {
  return [
    "div",
    { class: "embed-preview" },
    [
      "a",
      {
        href: data.url ?? data.embedUrl ?? "",
        target: "_blank",
        rel: "nofollow ugc noopener",
      },
      [
        "img",
        {
          class: "embed-oembed-photo",
          src: data.embedUrl ?? "",
          alt: data.title || "",
          style: getAspectRatioStyle(data),
          loading: "lazy",
        },
      ],
    ],
  ]
}

function renderMetadataCardDOM(container: HTMLElement, data: EmbedData): void {
  const [, , ...children] = renderMetadataCard(data) as [
    string,
    Record<string, string>,
    ...EmbedDomSpec[],
  ]
  for (const child of children) container.appendChild(domSpecToNode(child))
}

// ── Public functions ─────────────────────────────────────────────────────────

export function hasRichEmbed(data: EmbedData): boolean {
  if (data.oembedType === "photo" && data.embedUrl) return true
  if (data.embedUrl) return true
  if (data.embedHtml && data.embedWidth && data.embedHeight) return true
  if (data.oembedType === "video" && data.thumbnailUrl) return true
  return false
}

export function parseEmbedUrl(url: string): EmbedParseResult | null {
  try {
    new URL(url)
  } catch {
    return null
  }
  return {
    providerName: "oembed",
    url,
    embedId: url,
    embedType: null,
    embedUrl: "",
  }
}

// Decision tree (same for static + editor):
// 1. oembedType === "photo" && embedUrl  →  <img> wrapped in <a>
// 2. embedUrl                            →  <iframe src=embedUrl>
// 3. embedHtml && embedWidth && embedHeight  →  <iframe srcdoc=...> (XSS-safe)
// 4. oembedType === "video" && thumbnailUrl  →  thumbnail card with play button
// 5. else                                →  link card (shows thumbnail if available)
export function renderEmbedDomSpec(data: EmbedData): EmbedDomSpec {
  if (data.displayMode === "card") return renderMetadataCard(data)
  if (data.oembedType === "photo" && data.embedUrl)
    return renderPhotoEmbed(data)
  if (data.embedUrl) return renderIframe(data)
  if (data.embedHtml && data.embedWidth && data.embedHeight)
    return renderSrcdocIframe(data)
  if (data.oembedType === "video" && data.thumbnailUrl)
    return renderVideoThumbnail(data)
  return renderMetadataCard(data)
}

export function renderEditorPreview(
  container: HTMLElement,
  data: EmbedData,
): void {
  if (data.displayMode === "card") {
    renderMetadataCardDOM(container, data)
    return
  }

  if (data.oembedType === "photo" && data.embedUrl) {
    const link = document.createElement("a")
    link.href = data.url ?? data.embedUrl
    link.target = "_blank"
    link.rel = "nofollow ugc noopener"
    const img = document.createElement("img")
    img.src = data.embedUrl
    img.alt = data.title || ""
    img.classList.add("embed-oembed-photo")
    if (data.embedWidth && data.embedHeight)
      img.style.aspectRatio = `${data.embedWidth} / ${data.embedHeight}`
    img.loading = "lazy"
    link.appendChild(img)
    container.appendChild(link)
    return
  }

  if (data.embedUrl) {
    const iframe = document.createElement("iframe")
    iframe.src = data.embedUrl
    iframe.classList.add("embed-oembed-iframe")
    if (data.embedWidth && data.embedHeight) {
      iframe.style.aspectRatio = `${data.embedWidth} / ${data.embedHeight}`
    } else if (data.embedHeight) {
      iframe.style.height = `${data.embedHeight}px`
    } else {
      iframe.style.aspectRatio = "16 / 9"
    }
    iframe.sandbox.add(
      "allow-scripts",
      "allow-same-origin",
      "allow-forms",
      "allow-presentation",
    )
    iframe.allow =
      "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
    iframe.loading = "lazy"
    container.appendChild(iframe)

    const overlay = document.createElement("div")
    overlay.classList.add("embed-editor-overlay")
    container.appendChild(overlay)
    return
  }

  if (data.embedHtml && data.embedWidth && data.embedHeight) {
    const iframe = document.createElement("iframe")
    iframe.srcdoc = buildSrcdocHtml(data.embedHtml)
    iframe.classList.add("embed-oembed-iframe")
    iframe.style.aspectRatio = `${data.embedWidth} / ${data.embedHeight}`
    iframe.sandbox.add("allow-scripts")
    iframe.loading = "lazy"
    container.appendChild(iframe)

    const overlay = document.createElement("div")
    overlay.classList.add("embed-editor-overlay")
    container.appendChild(overlay)
    return
  }

  if (data.oembedType === "video" && data.thumbnailUrl) {
    const link = document.createElement("a")
    link.href = data.url ?? ""
    link.target = "_blank"
    link.rel = "nofollow ugc noopener"
    link.classList.add("embed-video-thumbnail")
    if (data.thumbnailWidth && data.thumbnailHeight) {
      link.style.aspectRatio = `${data.thumbnailWidth} / ${data.thumbnailHeight}`
      link.style.width = `min(100%, calc(800px * ${data.thumbnailWidth} / ${data.thumbnailHeight}))`
    }

    const img = document.createElement("img")
    img.src = data.thumbnailUrl
    img.alt = data.title || ""
    img.loading = "eager"
    img.classList.add("embed-video-thumbnail-img")
    link.appendChild(img)

    const playBtn = document.createElement("div")
    playBtn.classList.add("embed-video-play-button")
    playBtn.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">` +
      `<polygon points="5 3 19 12 5 21"/></svg>`
    link.appendChild(playBtn)

    if (data.faviconUrl) {
      const favicon = document.createElement("img")
      favicon.src = data.faviconUrl
      favicon.alt = ""
      favicon.loading = "eager"
      favicon.classList.add("embed-video-favicon")
      link.appendChild(favicon)
    }

    container.appendChild(link)
    return
  }

  renderMetadataCardDOM(container, data)
}

export function getEmbedEmailRender(data: EmbedData): EmbedEmailRender | null {
  if (!data.url) return null

  const hasEmbed =
    data.displayMode !== "card" && !!(data.embedUrl || data.embedHtml)

  if (hasEmbed) {
    return {
      type: "image-link",
      href: data.url,
      imageUrl: data.thumbnailUrl,
      imageAlt: data.title || "Embedded content",
      playLabel: data.embedType
        ? `Play on ${data.embedType}`
        : (data.title ?? "View content"),
      faviconUrl: data.faviconUrl,
    }
  }

  if (data.title || data.description || data.thumbnailUrl) {
    return {
      type: "styled-card",
      href: data.url,
      imageUrl: data.thumbnailUrl,
      title: data.title ?? data.url,
      description: data.description,
      faviconUrl: data.faviconUrl,
    }
  }

  return { type: "link", href: data.url }
}

export function toEmbedData(attrs: Record<string, any>): EmbedData {
  return {
    url: attrs.url ?? null,
    providerName: attrs.providerName ?? null,
    embedId: attrs.embedId ?? null,
    embedType: attrs.embedType ?? null,
    embedUrl: attrs.embedUrl ?? null,
    embedHtml: attrs.embedHtml ?? null,
    oembedType: attrs.oembedType ?? null,
    thumbnailUrl: attrs.thumbnailUrl ?? null,
    faviconUrl: attrs.faviconUrl ?? null,
    title: attrs.title ?? null,
    description: attrs.description ?? null,
    embedWidth: attrs.embedWidth ?? null,
    embedHeight: attrs.embedHeight ?? null,
    thumbnailWidth: attrs.thumbnailWidth ?? null,
    thumbnailHeight: attrs.thumbnailHeight ?? null,
    displayMode: attrs.displayMode ?? null,
  }
}

// ── Tiptap Node ──────────────────────────────────────────────────────────────

export const EMBED_NODE = "embed"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    embed: {
      insertEmbed: (attrs?: {
        url?: string
        providerName?: string | null
        embedId?: string | null
        embedType?: string | null
        embedUrl?: string | null
        embedHtml?: string | null
        oembedType?: string | null
        thumbnailUrl?: string | null
        faviconUrl?: string | null
        title?: string | null
        description?: string | null
        embedWidth?: number | null
        embedHeight?: number | null
        thumbnailWidth?: number | null
        thumbnailHeight?: number | null
        displayMode?: string | null
      }) => ReturnType
    }
  }
}

function attr(tiptapName: string, htmlSuffix: string) {
  return {
    default: null as string | null,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute(`data-${htmlSuffix}`),
    renderHTML: (attributes: Record<string, any>) =>
      attributes[tiptapName]
        ? { [`data-${htmlSuffix}`]: attributes[tiptapName] }
        : {},
  }
}

function encodeHtmlAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;")
}

export const EmbedNode = Node.create({
  name: EMBED_NODE,
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      url: attr("url", "url"),
      providerName: attr("providerName", "provider"),
      embedId: attr("embedId", "embed-id"),
      embedType: attr("embedType", "embed-type"),
      embedUrl: attr("embedUrl", "embed-url"),
      embedHtml: {
        default: null as string | null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-embed-html"),
        renderHTML: (attributes: Record<string, any>) =>
          attributes.embedHtml
            ? { "data-embed-html": encodeHtmlAttr(attributes.embedHtml) }
            : {},
      },
      oembedType: attr("oembedType", "oembed-type"),
      thumbnailUrl: attr("thumbnailUrl", "thumbnail-url"),
      faviconUrl: attr("faviconUrl", "favicon-url"),
      title: attr("title", "title"),
      description: attr("description", "description"),
      embedWidth: {
        default: null as number | null,
        parseHTML: (element: HTMLElement) => {
          const val = element.getAttribute("data-embed-width")
          return val ? Number.parseInt(val, 10) : null
        },
        renderHTML: (attributes: Record<string, any>) =>
          attributes.embedWidth
            ? { "data-embed-width": String(attributes.embedWidth) }
            : {},
      },
      embedHeight: {
        default: null as number | null,
        parseHTML: (element: HTMLElement) => {
          const val = element.getAttribute("data-embed-height")
          return val ? Number.parseInt(val, 10) : null
        },
        renderHTML: (attributes: Record<string, any>) =>
          attributes.embedHeight
            ? { "data-embed-height": String(attributes.embedHeight) }
            : {},
      },
      thumbnailWidth: {
        default: null as number | null,
        parseHTML: (element: HTMLElement) => {
          const val = element.getAttribute("data-thumbnail-width")
          return val ? Number.parseInt(val, 10) : null
        },
        renderHTML: (attributes: Record<string, any>) =>
          attributes.thumbnailWidth
            ? { "data-thumbnail-width": String(attributes.thumbnailWidth) }
            : {},
      },
      thumbnailHeight: {
        default: null as number | null,
        parseHTML: (element: HTMLElement) => {
          const val = element.getAttribute("data-thumbnail-height")
          return val ? Number.parseInt(val, 10) : null
        },
        renderHTML: (attributes: Record<string, any>) =>
          attributes.thumbnailHeight
            ? { "data-thumbnail-height": String(attributes.thumbnailHeight) }
            : {},
      },
      displayMode: attr("displayMode", "display"),
    }
  },

  parseHTML() {
    return [{ tag: `figure[data-type="${EMBED_NODE}"]` }]
  },

  renderHTML({ HTMLAttributes, node }) {
    const attrs = mergeAttributes({ "data-type": EMBED_NODE }, HTMLAttributes)
    const innerContent = renderEmbedDomSpec(toEmbedData(node.attrs))
    return ["figure", attrs, innerContent]
  },

  addCommands() {
    return {
      insertEmbed:
        (attrs = {}) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: toEmbedData(attrs),
          }),
    }
  },
})
